import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { motion } from "framer-motion";
import { money } from "@/lib/api";
import { useApp } from "@/context/AppContext";

export default function ProductCard({ product, index = 0 }) {
  const { toggleWishlist, inWishlist } = useApp();
  const wished = inWishlist(product.id);

  const hasDiscount =
    product.discount_price && product.discount_price > 0 && product.discount_price < product.price;
  const stock = Number(product.stock ?? 0);
  const isOut = stock <= 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      className="group"
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative overflow-hidden bg-muted aspect-[4/5] product-img-hover">
        <Link to={`/product/${product.id}`} data-testid={`product-link-${product.id}`}>
          <img
            src={product.image}
            alt={product.name}
            className={`w-full h-full object-cover ${isOut ? "opacity-70" : ""}`}
            loading="lazy"
          />
        </Link>

        {hasDiscount && !isOut && (
          <div
            className="absolute top-3 left-3 bg-foreground text-background px-3 py-1 label-xs"
            data-testid={`product-badge-sale-${product.id}`}
          >
            SALE
          </div>
        )}
        {isOut && (
          <div
            className="absolute inset-0 grid place-items-center bg-background/60"
            data-testid={`product-out-of-stock-${product.id}`}
          >
            <span className="border border-foreground bg-background px-4 py-2 label-xs tracking-[0.3em]">
              SOLD OUT
            </span>
          </div>
        )}

        <button
          onClick={(e) => {
            e.preventDefault();
            toggleWishlist(product);
          }}
          className="absolute top-3 right-3 w-9 h-9 grid place-items-center bg-background/90 hover:bg-background transition-colors"
          aria-label="Add to wishlist"
          data-testid={`wishlist-btn-${product.id}`}
          style={{ transitionProperty: "background-color, color" }}
        >
          <Heart className={`h-4 w-4 ${wished ? "fill-foreground" : ""}`} />
        </button>

        {!isOut && (
          <div
            className="absolute inset-x-0 bottom-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300"
            style={{ transitionProperty: "transform" }}
          >
            <Link
              to={`/product/${product.id}`}
              className="block bg-foreground text-background text-center py-3 label-xs"
            >
              Quick View
            </Link>
          </div>
        )}
      </div>

      <div className="mt-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Link
            to={`/product/${product.id}`}
            className="font-serif text-lg leading-tight hover:opacity-70 block truncate"
            style={{ transitionProperty: "opacity" }}
          >
            {product.name}
          </Link>
          <div className="label-xs opacity-60 mt-1 truncate">{product.category || ""}</div>
        </div>
        <div className="text-right shrink-0" data-testid={`product-price-${product.id}`}>
          {hasDiscount ? (
            <div className="flex flex-col items-end leading-tight">
              <span className="font-body text-base">{money(product.discount_price)}</span>
              <span className="text-xs opacity-50 line-through">{money(product.price)}</span>
            </div>
          ) : (
            <span className="font-body text-base">{money(product.price)}</span>
          )}
        </div>
      </div>
    </motion.div>
  );
}
